import { useState, useEffect } from "react";
import { axiosRequest } from "../../utility/axios";
import { QUALIFYING_LEVELS, qualifyingLevelFor, PAYOUT_SPLIT, CONTRIBUTION } from "./rules.js";

// Public, phone-first page opened by scanning the QR on the /bbj-display screen: the
// full official rules, with the live jackpot and today's qualifying hand highlighted.

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@900&family=Cinzel:wght@700&display=swap');
.bbr-root{min-height:100vh;background:radial-gradient(ellipse at 30% 0%,#0a1628 0%,#000814 55%,#000 100%);
  color:#e8dcc0;font-family:'Georgia',serif;padding:24px 16px 48px;}
.bbr-wrap{max-width:680px;margin:0 auto;}
.bbr-club{text-align:center;font-family:'Cinzel','Georgia',serif;font-size:12px;letter-spacing:6px;color:rgba(255,215,0,.6);text-transform:uppercase;}
.bbr-title{text-align:center;font-family:'Cinzel Decorative','Georgia',serif;font-weight:900;font-size:clamp(24px,7vw,40px);line-height:1.15;margin:8px 0 2px;
  background:linear-gradient(90deg,#9a6f00,#FFD700,#FFEC8B,#FFA500,#FFD700,#9a6f00);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
.bbr-sub{text-align:center;font-family:'Cinzel','Georgia',serif;font-size:13px;letter-spacing:4px;color:rgba(255,215,0,.5);margin-bottom:20px;}

.bbr-live{border:1px solid rgba(255,215,0,.45);border-radius:16px;background:rgba(255,215,0,.06);padding:16px;text-align:center;margin-bottom:24px;}
.bbr-live-label{font-size:11px;letter-spacing:4px;color:rgba(255,215,0,.6);text-transform:uppercase;}
.bbr-live-amt{font-family:'Cinzel Decorative','Georgia',serif;font-weight:900;font-size:clamp(34px,11vw,56px);color:#FFD700;line-height:1.1;margin:4px 0 8px;}
.bbr-live-hand{font-size:15px;color:#fff;}
.bbr-live-hand b{color:#FFD700;}

.bbr-sec{margin-top:26px;}
.bbr-h{font-family:'Cinzel','Georgia',serif;font-size:15px;letter-spacing:3px;color:#FFD700;text-transform:uppercase;
  padding-bottom:6px;border-bottom:1px solid rgba(255,215,0,.25);margin-bottom:12px;}
.bbr-h3{font-family:'Cinzel','Georgia',serif;font-size:13px;letter-spacing:2px;color:rgba(255,215,0,.8);margin:14px 0 8px;}
.bbr-p{font-size:15px;line-height:1.6;margin:8px 0;}
.bbr-p b,.bbr-li b{color:#fff;}
.bbr-ul{margin:8px 0;padding-left:20px;}
.bbr-li{font-size:15px;line-height:1.6;margin:6px 0;}
.bbr-big{text-align:center;font-family:'Cinzel Decorative','Georgia',serif;font-weight:900;font-size:28px;color:#FFD700;margin:6px 0;}

.bbr-table{width:100%;border-collapse:collapse;font-size:15px;margin:10px 0;}
.bbr-table th{text-align:left;font-weight:normal;font-size:11px;letter-spacing:2px;text-transform:uppercase;color:rgba(255,215,0,.6);padding:6px 10px;border-bottom:1px solid rgba(255,215,0,.25);}
.bbr-table td{padding:9px 10px;border-bottom:1px solid rgba(255,255,255,.07);}
.bbr-table td.num{text-align:right;font-variant-numeric:tabular-nums;white-space:nowrap;width:1%;}
.bbr-table tr.cur td{background:rgba(255,215,0,.14);color:#FFD700;font-weight:bold;}
.bbr-tag{display:inline-block;margin-left:6px;font-size:10px;letter-spacing:1px;padding:1px 6px;border-radius:6px;background:#FFD700;color:#000;vertical-align:middle;}

.bbr-ways{counter-reset:way;list-style:none;padding:0;margin:8px 0;}
.bbr-ways li{border:1px solid rgba(255,215,0,.18);border-radius:12px;padding:12px 14px;margin:8px 0;font-size:15px;line-height:1.5;}
.bbr-ways li b{color:#FFD700;}
.bbr-ways .ex{display:block;color:rgba(232,220,192,.65);font-size:13.5px;margin-top:2px;}

.bbr-payout{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin:10px 0;}
.bbr-pay{border:1px solid rgba(255,215,0,.3);border-radius:12px;padding:12px;text-align:center;}
.bbr-pay-pct{font-family:'Cinzel Decorative','Georgia',serif;font-weight:900;font-size:28px;color:#FFD700;line-height:1.1;}
.bbr-pay-lbl{font-size:12px;letter-spacing:2px;text-transform:uppercase;color:rgba(232,220,192,.8);margin-top:2px;}

.bbr-final{margin-top:30px;border:1px solid rgba(255,215,0,.45);border-radius:14px;padding:16px;text-align:center;background:rgba(255,215,0,.05);}
.bbr-foot{text-align:center;margin-top:28px;font-size:11px;letter-spacing:4px;color:rgba(255,215,0,.3);text-transform:uppercase;}
`;

export default function BBJRulesPage() {
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    const style = document.createElement("style");
    style.id = "bbr-css";
    style.textContent = CSS;
    document.head.appendChild(style);
    return () => document.getElementById("bbr-css")?.remove();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await axiosRequest("GET", `${process.env.REACT_APP_API_URL}/bbj/public`, null);
        const amt = r?.data?.data?.bbj_amount;
        if (amt != null) setAmount(amt);
      } catch {}
    })();
  }, []);

  const current = amount != null ? qualifyingLevelFor(amount) : null;
  const fmt = (n) => Number(n).toLocaleString("en-IN");

  return (
    <div className="bbr-root">
      <div className="bbr-wrap">
        <div className="bbr-club">♠ Real Club ♠</div>
        <h1 className="bbr-title">Bad Beat Jackpot</h1>
        <div className="bbr-sub">Official Rules</div>

        {current && (
          <div className="bbr-live">
            <div className="bbr-live-label">Current Jackpot</div>
            <div className="bbr-live-amt">{fmt(amount)}</div>
            <div className="bbr-live-hand">Qualifying hand right now: <b>{current.hand}</b> must be beaten</div>
          </div>
        )}

        <section className="bbr-sec">
          <div className="bbr-h">Jackpot Starting Point</div>
          <div className="bbr-big">1,00,000</div>
        </section>

        <section className="bbr-sec">
          <div className="bbr-h">Qualifying Hand</div>
          <p className="bbr-p">At <b>1,00,000</b>, the qualifying Bad Beat is: <b>Quad Eights (8888x) must be beaten.</b></p>
          <p className="bbr-p">The qualifying hand decreases by one rank each time the jackpot increases by <b>1,00,000</b>:</p>
          <table className="bbr-table">
            <thead><tr><th style={{ textAlign: "right" }}>Jackpot</th><th>Qualifying Hand</th></tr></thead>
            <tbody>
              {QUALIFYING_LEVELS.map((l) => (
                <tr key={l.min} className={current === l ? "cur" : ""}>
                  <td className="num">{l.label}</td>
                  <td>{l.hand}{current === l && <span className="bbr-tag">NOW</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="bbr-p">At <b>8,00,000 and above</b>, the qualifying hand remains <b>Aces Full of Kings (AAAKK)</b> while the jackpot continues to accumulate.</p>
        </section>

        <section className="bbr-sec">
          <div className="bbr-h">Hand Requirements</div>
          <div className="bbr-h3">Both Hole Cards Must Play</div>
          <p className="bbr-p">For a hand to qualify for the Bad Beat Jackpot:</p>
          <ul className="bbr-ul">
            <li className="bbr-li"><b>Both hole cards of the losing player must be used</b> in the final five-card hand.</li>
            <li className="bbr-li">The qualifying hand must be made using <b>both hole cards</b>.</li>
            <li className="bbr-li">The losing hand must be beaten at showdown.</li>
          </ul>
        </section>

        <section className="bbr-sec">
          <div className="bbr-h">Qualifying Ways to Win</div>
          <p className="bbr-p">The qualifying Bad Beat may occur when:</p>
          <ol className="bbr-ways">
            <li><b>1. Quads are beaten by higher Quads</b><span className="ex">Example: Quad 8s loses to Quad 9s.</span></li>
            <li><b>2. Quads are beaten by a Straight Flush</b><span className="ex">Example: Quad 8s loses to a Straight Flush.</span></li>
            <li><b>3. At the 8,00,000+ level:</b> <b>Aces Full of Kings (AAAKK)</b> must be beaten by a qualifying higher-ranking hand, subject to both hole cards playing.</li>
          </ol>
          <p className="bbr-p">The winning hand must also satisfy the applicable <b>both-hole-cards-must-play</b> requirement where required by the club's jackpot rules.</p>
        </section>

        <section className="bbr-sec">
          <div className="bbr-h">Jackpot Contribution</div>
          <p className="bbr-p"><b>{CONTRIBUTION.amount}</b> is contributed to the Bad Beat Jackpot from every qualifying pot <b>over {CONTRIBUTION.potOver}</b>.</p>
          <p className="bbr-p">The contribution continues while the jackpot is active and accumulating.</p>
        </section>

        <section className="bbr-sec">
          <div className="bbr-h">Jackpot Payout</div>
          <p className="bbr-p">When a qualifying Bad Beat occurs, the jackpot is distributed as follows:</p>
          <div className="bbr-payout">
            {PAYOUT_SPLIT.map((p) => (
              <div className="bbr-pay" key={p.label}>
                <div className="bbr-pay-pct">{p.pct}%</div>
                <div className="bbr-pay-lbl">{p.label}</div>
              </div>
            ))}
          </div>
          <div className="bbr-h3">Table Share</div>
          <p className="bbr-p">The <b>25% Table Share</b> is distributed among the eligible players at the table according to the club's payout procedure.</p>
        </section>

        <section className="bbr-sec">
          <div className="bbr-h">Table Conduct &amp; Discipline</div>
          <ul className="bbr-ul">
            <li className="bbr-li">Players must <b>not discuss, reveal, or comment on their hands while the hand is in progress</b>.</li>
            <li className="bbr-li">Players must not say <b>“Bad Beat”</b>, announce a possible jackpot situation, or otherwise draw attention to the jackpot while action is pending.</li>
            <li className="bbr-li">Any attempt to influence the action, disclose information, signal another player, or otherwise interfere with the hand may result in <b>disqualification from the Bad Beat Jackpot</b>.</li>
            <li className="bbr-li">Collusion, soft play, angle-shooting, cheating, or other serious misconduct may result in disqualification and further action under club rules.</li>
          </ul>
        </section>

        <section className="bbr-sec">
          <div className="bbr-h">Verification</div>
          <ul className="bbr-ul">
            <li className="bbr-li">The qualifying hand must reach <b>showdown</b>.</li>
            <li className="bbr-li">Both players' cards must be available for verification.</li>
            <li className="bbr-li">The dealer and/or club management will verify the final hands and confirm that all jackpot conditions have been met.</li>
            <li className="bbr-li">The club may review the hand, table action, and relevant circumstances before approving the payout.</li>
            <li className="bbr-li">If any qualifying condition is not satisfied, the hand will not qualify for the jackpot.</li>
          </ul>
        </section>

        <div className="bbr-final">
          <div className="bbr-h" style={{ borderBottom: "none", marginBottom: 6 }}>Final Decision</div>
          <p className="bbr-p">All Bad Beat Jackpot decisions are subject to verification by <b>REAL CLUB management</b>.</p>
          <p className="bbr-p"><b>Management's decision regarding qualification, eligibility, and payout shall be final.</b></p>
        </div>

        <div className="bbr-foot">♠ ♥ Real Club · Bad Beat Jackpot ♦ ♣</div>
      </div>
    </div>
  );
}
